// src/components/DashboardLayout.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  getOrgSelectorPresentation,
  resolveDashboardQueryOrgId,
  useAuth,
} from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import Sidebar from "./Sidebar";
import TermsGate from "./TermsGate"; // ← IMPORTANT: correct relative path
import pkg from "../../package.json";
import {
  Menu,
  Bell,
  MessageCircle,
  HelpCircle,
  Home,
  MessageSquare,
  Gamepad2,
} from "lucide-react";
import { fetchOrgProfile, fetchOrgSessions, getDefaultOrgTheme } from "../utils/orgData";
import { applyTheme, getThemeModeTokens } from "../theme";
import { DEFAULT_ORG_ID, getOrgBrandAssets, getOrgLogoAsset } from "../utils/brandAssets";
import {
  buildCoachThreadSessionList,
  COACH_THREAD_SYNC_EVENT,
  getCoachThreadIncomingAt,
  getCoachThreadSessionId,
  listenCoachThread,
  markCoachThreadRead,
  sendCoachThreadMessage,
} from "../utils/coachThread";
import { fetchOrgActivity } from "../utils/orgAudit";
import { createSupportTicket, fetchSupportTickets } from "../utils/support";
import { notifySupportForOrg } from "../utils/notifySupport";
import {
  dismissNotification,
  NOTIFICATION_READ_EVENT,
  getReadNotificationIds,
  handleNotificationTap,
  notificationReadStorageKey,
} from "../utils/notificationNavigation";
import { processFlagCaseWorkflows } from "../utils/flagCases";
import { getCoachThreadSenderLabel, getSessionDisplayName } from "../utils/sessionPrivacy";
import {
  filterVisibleNotifications,
  getNotificationMeta,
  getNotificationTitle,
} from "../utils/notificationPrivacy";

const BG_KEY = "pref_bg"; // written by PreferencesPage
const bgKeyForOrg = (orgId) => `${BG_KEY}__${orgId || DEFAULT_ORG_ID}`;
const ICON_REFRESH_HINT_KEY = "nia_icon_refresh_hint";
const normalizeBgClass = (value) => {
  if (value === "bg-none" || value === "bg-photo") return value;
  return "bg-hero-grid";
};
const readBgClassForOrg = (orgId) => {
  try {
    return normalizeBgClass(
      localStorage.getItem(bgKeyForOrg(orgId)) ||
      localStorage.getItem(BG_KEY) ||
      "bg-hero-grid"
    );
  } catch {
    return "bg-hero-grid";
  }
};
const readCachedOrgProfile = (orgId) => {
  try {
    const raw = JSON.parse(localStorage.getItem(`org_profile__${orgId}`) || "{}");
    if (!raw || typeof raw !== "object") return null;
    return raw;
  } catch {
    return null;
  }
};
export default function DashboardLayout() {
  const {
    user,
    activeOrgId,
    loading,
    orgs,
    orgLoadStatus,
    orgLoadError,
    orgSelectionError,
    orgAccessReady,
    firestoreError,
    clearFirestoreError,
  } = useAuth();
  const { isDark, mode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [activityRows, setActivityRows] = useState([]);
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageSessions, setMessageSessions] = useState([]);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportCount, setSupportCount] = useState(0);
  const [supportCategory, setSupportCategory] = useState("Bug");
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportStatus, setSupportStatus] = useState("");
  const [supportSending, setSupportSending] = useState(false);
  const [popupSession, setPopupSession] = useState(null);
  const [popupCollapsed, setPopupCollapsed] = useState(false);
  const [popupThread, setPopupThread] = useState([]);
  const [popupInput, setPopupInput] = useState("");
  const [popupSending, setPopupSending] = useState(false);
  const [iconRefreshHint, setIconRefreshHint] = useState(false);
  const [useLandscapeSplash, setUseLandscapeSplash] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(min-width: 900px), (min-aspect-ratio: 6/5)").matches;
  });
  const [orgProfile, setOrgProfile] = useState(() => ({
    name: "",
    logoUrl: "",
    logoDarkUrl: "",
    orgId: DEFAULT_ORG_ID,
    theme: getDefaultOrgTheme(),
    brandColors: null,
    hasCustomTheme: false,
  }));

  const [bgClass, setBgClass] = useState(() => readBgClassForOrg(DEFAULT_ORG_ID));
  const [navOpen, setNavOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 960 : true));
  const [isDesktop, setIsDesktop] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 960 : true));
  const [readNotifIds, setReadNotifIds] = useState(() => new Set());
  const dataOrgId = resolveDashboardQueryOrgId(
    user,
    activeOrgId,
    orgAccessReady
  );
  const selectorPresentation = getOrgSelectorPresentation(
    orgLoadStatus,
    orgs
  );
  const currentNotifReadKey = useMemo(
    () => notificationReadStorageKey(user?.uid, dataOrgId || "org_default"),
    [user?.uid, dataOrgId]
  );

  useEffect(() => {
    const currentOrgId = dataOrgId || DEFAULT_ORG_ID;
    const onPref = () => {
      setBgClass(readBgClassForOrg(currentOrgId));
    };
    onPref();
    window.addEventListener("pref-changed", onPref);
    window.addEventListener("nia-theme", onPref);
    window.addEventListener("storage", onPref);
    return () => {
      window.removeEventListener("pref-changed", onPref);
      window.removeEventListener("nia-theme", onPref);
      window.removeEventListener("storage", onPref);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataOrgId]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    document.body.classList.toggle("dark", isDark);
    document.documentElement.setAttribute("data-theme", mode);
    document.body.setAttribute("data-theme", mode);
  }, [isDark, mode, location.pathname]);

  useEffect(() => {
    const onResize = () => {
      const desktop = window.innerWidth >= 960;
      setIsDesktop(desktop);
      if (desktop) setNavOpen(true); // keep sidebar visible on wide screens
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(min-width: 900px), (min-aspect-ratio: 6/5)");
    const sync = () => setUseLandscapeSplash(media.matches);
    sync();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", sync);
      return () => media.removeEventListener("change", sync);
    }
    media.addListener(sync);
    return () => media.removeListener(sync);
  }, []);

  useEffect(() => {
    try {
      setIconRefreshHint(localStorage.getItem(ICON_REFRESH_HINT_KEY) === "1");
    } catch {}
  }, []);

  useEffect(() => {
    const hydrate = () => setReadNotifIds(getReadNotificationIds(currentNotifReadKey));
    const onRead = (event) => {
      const key = String(event?.detail?.readKey || "");
      if (key && key !== currentNotifReadKey) return;
      hydrate();
    };
    hydrate();
    window.addEventListener(NOTIFICATION_READ_EVENT, onRead);
    return () => window.removeEventListener(NOTIFICATION_READ_EVENT, onRead);
  }, [currentNotifReadKey]);

  const markSessionThreadViewed = useCallback((session) => {
    if (!user?.uid) return;
    const sessionThreadId = getCoachThreadSessionId(session);
    const incomingAt = getCoachThreadIncomingAt(session, user?.role || "member");
    if (!sessionThreadId || !incomingAt) return;
    markCoachThreadRead(user.uid, sessionThreadId, incomingAt);
    setMessageSessions((prev) => prev.map((entry) => (
      String(getCoachThreadSessionId(entry)) === sessionThreadId
        ? { ...entry, _unread: false }
        : entry
    )));
  }, [user]);

  useEffect(() => {
    let mounted = true;
    if (!user?.uid) return undefined;
    const orgId = dataOrgId;
    if (!orgId) return undefined;
    const refresh = () => {
      fetchOrgSessions(user, orgId)
        .then((sessions) => {
          if (!mounted) return;
          const threadSessions = buildCoachThreadSessionList(sessions, user, user?.role || "member");
          setMessageSessions(threadSessions.slice(0, 5));
          setPopupSession((prev) => {
            if (!prev) return prev;
            const matched = threadSessions.find((row) => String(getCoachThreadSessionId(row)) === String(getCoachThreadSessionId(prev)));
            return matched || prev;
          });
        })
        .catch(() => {});
    };
    refresh();
    const onSync = () => refresh();
    window.addEventListener(COACH_THREAD_SYNC_EVENT, onSync);
    const id = setInterval(refresh, 30000);
    return () => {
      mounted = false;
      window.removeEventListener(COACH_THREAD_SYNC_EVENT, onSync);
      clearInterval(id);
    };
  }, [user, dataOrgId]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const role = String(user.role || "").toLowerCase();
    const canProcess = role === "coach" || role === "admin" || role === "superadmin";
    if (!canProcess) return undefined;
    const orgId = dataOrgId;
    if (!orgId) return undefined;
    const run = () => {
      processFlagCaseWorkflows(user, orgId).catch(() => {});
    };
    run();
    const id = setInterval(run, 15 * 60 * 1000);
    return () => clearInterval(id);
  }, [user, dataOrgId]);

  useEffect(() => {
    let mounted = true;
    if (!user?.uid || user.role !== "superadmin") return undefined;
    const orgId = dataOrgId;
    if (!orgId) return undefined;
    const refresh = () => {
      fetchSupportTickets(user, orgId, { status: "open", max: 50 })
        .then((rows) => {
          if (!mounted) return;
          setSupportCount(Array.isArray(rows) ? rows.length : 0);
        })
        .catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 30000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [user, dataOrgId]);

  useEffect(() => {
    let mounted = true;
    if (!user?.uid) return undefined;
    const orgId = dataOrgId;
    if (!orgId) return undefined;
    const refresh = () => {
      fetchOrgActivity(user, orgId, 15)
        .then((rows) => {
          if (!mounted) return;
          setActivityRows(Array.isArray(rows) ? rows : []);
        })
        .catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 30000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [user, dataOrgId]);

  const notificationRows = useMemo(() => {
    if (!user?.uid) return [];
    return filterVisibleNotifications(activityRows, user, readNotifIds);
  }, [activityRows, user, readNotifIds]);

  const unreadCount = useMemo(() => notificationRows.length, [notificationRows]);
  const messageCount = useMemo(
    () => messageSessions.filter((session) => session._unread).length,
    [messageSessions]
  );

  const mobileNavItems = useMemo(() => {
    const role = user?.role || "member";
    const items = [];
    const add = (key, label, path, icon, action, badge) => {
      items.push({ key, label, path, icon, action, badge });
    };
    const openMore = () => setNavOpen((v) => !v);
    const openNotifs = () => {
      if (isDesktop) {
        setNotifOpen((prev) => !prev);
        setMessageOpen(false);
        setSupportOpen(false);
        return;
      }
      navigate("/notifications");
    };
    const homePath = role === "coach" ? "/coach" : "/dashboard";

    add("home", "Home", homePath, <Home size={20} />);
    add("chat", "Start Session", "/chat", <MessageSquare size={20} />);
    add("games", "Games", "/games", <Gamepad2 size={20} />);
    add("notif", "Notifications", "/notifications", <Bell size={20} />, openNotifs, unreadCount);
    add("more", "Menu", "", <Menu size={20} />, openMore);

    return items;
  }, [user?.role, unreadCount, isDesktop, navigate]);

  const getSessionTitle = (session) => {
    if (!session) return "Member";
    if (user?.role === "member") {
      return "Coach";
    }
    return getSessionDisplayName(session, user?.role || "member");
  };
  const getSessionPreview = (session) => {
    if (!session) return "";
    if (user?.role === "member") {
      return session.lastCoachMessageBy ? "New coach message" : "Coach thread";
    }
    return session.lastMemberMessageBy ? "New member reply" : "Member thread";
  };
  const getSessionTime = (session) => {
    const ts = session?.lastCoachMessageAt || session?.createdAt || session?.date || Date.now();
    return new Date(ts).toLocaleString();
  };
  const handleNotifTap = (row) => {
    const id = String(row?.id || "").trim();
    if (id) {
      setReadNotifIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    }
    handleNotificationTap({
      navigate,
      row,
      readKey: currentNotifReadKey,
      currentPathname: location.pathname,
    });
  };

  const handleNotifDismiss = (event, row) => {
    event.stopPropagation();
    dismissNotification(currentNotifReadKey, row);
    const id = String(row?.id || "").trim();
    if (id) {
      setReadNotifIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    }
  };

  const submitSupport = async () => {
    if (!supportSubject.trim() || !supportMessage.trim()) {
      setSupportStatus("Please add a subject and message.");
      return;
    }
    if (!user?.uid) return;
    setSupportStatus("Submitting...");
    setSupportSending(true);
    const orgId = dataOrgId;
    if (!orgId) {
      setSupportStatus("Choose an authorised organisation first.");
      setSupportSending(false);
      return;
    }
    const ticket = await createSupportTicket(user, orgId, {
      category: supportCategory,
      subject: supportSubject.trim(),
      message: supportMessage.trim(),
    });
    if (!ticket) {
      setSupportStatus("Unable to submit. Please try again.");
      setSupportSending(false);
      return;
    }
    notifySupportForOrg(user, orgId, {
      subject: `Support request • ${ticket.subject}`,
      message: `${ticket.userName || "User"} (${ticket.userEmail || "unknown"})\nCategory: ${ticket.category}\n\n${supportMessage.trim()}`,
    }).catch(() => {});
    setSupportStatus("Submitted. Support will respond soon.");
    setSupportSending(false);
    setSupportSubject("");
    setSupportMessage("");
    if (user.role === "superadmin") {
      setSupportCount((c) => c + 1);
    }
    setTimeout(() => {
      setSupportStatus("");
      setSupportOpen(false);
    }, 600);
  };

  useEffect(() => {
    if (!popupSession || !user?.uid) return;
    const orgId = dataOrgId;
    if (!orgId) return;
    const sessionId = popupSession.id || popupSession.createdAt;
    if (!sessionId) return;
    markSessionThreadViewed(popupSession);
    const unsub = listenCoachThread(user, orgId, sessionId, (rows) => {
      setPopupThread(rows || []);
    });
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [popupSession, user, dataOrgId, markSessionThreadViewed]);

  useEffect(() => {
    let mounted = true;
    if (!user?.uid) return undefined;
    const orgId = dataOrgId;
    if (!orgId) return undefined;
    const cached = readCachedOrgProfile(orgId);
    setOrgProfile((prev) => {
      if (cached) {
        return {
          ...prev,
          ...cached,
          orgId,
          logoUrl: cached.logoUrl || "",
          logoDarkUrl: cached.logoDarkUrl || cached.logoUrlDark || cached.darkLogoUrl || "",
        };
      }
      if (prev?.orgId === orgId) return prev;
      return {
        name: prev?.name || "",
        logoUrl: prev?.logoUrl || "",
        logoDarkUrl: prev?.logoDarkUrl || "",
        orgId,
        theme: getDefaultOrgTheme(),
        brandColors: prev?.brandColors || null,
        hasCustomTheme: Boolean(prev?.hasCustomTheme),
      };
    });
    fetchOrgProfile(user, orgId)
      .then((profile) => {
        if (!mounted) return;
        setOrgProfile({
          name: profile?.name || "",
          logoUrl: profile?.logoUrl || "",
          logoDarkUrl: profile?.logoDarkUrl || "",
          orgId,
          theme: profile?.theme || getDefaultOrgTheme(),
          brandColors: profile?.brandColors || null,
          hasCustomTheme: Boolean(profile?.hasCustomTheme),
        });
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [user, dataOrgId]);

  const currentOrgId = dataOrgId || DEFAULT_ORG_ID;
  const theme = orgProfile?.orgId === currentOrgId ? (orgProfile?.theme || getDefaultOrgTheme()) : getDefaultOrgTheme();
  const activeTokens = getThemeModeTokens(theme, isDark ? "dark" : "light");
  const isImmersiveChatRoute = location.pathname === "/chat";

  useEffect(() => {
    applyTheme(activeTokens);
  }, [activeTokens]);

  if (loading) {
    const loadingOrgId = activeOrgId || user?.orgId || DEFAULT_ORG_ID;
    const loadingBrand = getOrgBrandAssets(loadingOrgId, mode);
    const useLandscape = useLandscapeSplash;
    const loadingSplash = useLandscape
      ? loadingBrand.splashLandscapeUrl
      : loadingBrand.splashUrl;
    const loadingSplashFallback = useLandscape
      ? loadingBrand.splashLandscapeFallbackUrl
      : loadingBrand.splashFallbackUrl;
    return (
      <div className="loading-splash-screen" data-theme={mode}>
        {loadingSplash ? (
          <picture className="loading-splash-media">
            <source srcSet={loadingSplash} type="image/png" />
            <img
              src={loadingSplashFallback || loadingSplash}
              alt="Loading Nia COACH"
              className="loading-splash-image"
            />
          </picture>
        ) : (
          <div className="loading-splash-fallback" />
        )}
        <div className="loading-splash-message">Loading account…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const orgId = dataOrgId || DEFAULT_ORG_ID;
  const primaryLogo = getOrgLogoAsset({
    orgId,
    logoUrl: orgProfile?.logoUrl,
    logoDarkUrl: orgProfile?.logoDarkUrl,
    mode,
    kind: "logo",
  });
  const compactLogo = getOrgLogoAsset({
    orgId,
    logoUrl: orgProfile?.logoUrl,
    logoDarkUrl: orgProfile?.logoDarkUrl,
    mode,
    compact: true,
  });

  const style = {
    "--bg-grid-image": "url('/brand/hero-grid.svg')",
    "--bg-photo-image": mode === "dark"
      ? "url('/brand/nia/nia-login-dark.jpg')"
      : "url('/brand/nia/nia-login-light.jpg')",
  };
  const appVersion = pkg?.version || "1.0.0";

  return (
    <div className={`app-surface ${bgClass}`} style={style} data-theme={mode}>
      {/* Terms shown only after login */}
      <TermsGate version="v1" policy="perUser" showBeforeLogin={false} allowSkip={false} />
      {firestoreError && (
        <div className="nia-alert">
          <div>
            <strong>Firestore notice:</strong> {firestoreError}
          </div>
          <button className="btn btn-ghost" onClick={clearFirestoreError}>Dismiss</button>
        </div>
      )}
      {iconRefreshHint && isDesktop && (
        <div className="nia-alert nia-alert--app-update">
          <div>
            <strong>App updated:</strong> If your home-screen icon still looks old, remove and re-add the app once.
          </div>
          <button
            className="btn btn-ghost"
            onClick={() => {
              setIconRefreshHint(false);
              try { localStorage.removeItem(ICON_REFRESH_HINT_KEY); } catch {}
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {!isImmersiveChatRoute && (
      <div className="mobile-topbar">
        <div className="topbar-left">
          <img className="org-logo org-logo--compact" src={compactLogo} alt="Org logo" />
        </div>
        <div className="topbar-center">
          <span>{orgProfile?.name || "Dashboard"}</span>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="icon-btn topbar-inbox"
            aria-label="Inbox"
            onClick={() => {
              if (!isDesktop) {
                navigate("/inbox");
                return;
              }
              setMessageOpen((v) => !v);
              setNotifOpen(false);
            }}
          >
            <MessageCircle size={20} />
            {messageCount > 0 && <span className="topbar-badge">{messageCount}</span>}
          </button>
          <button
            type="button"
            className="icon-btn topbar-support"
            aria-label="Support"
            onClick={() => setSupportOpen((v) => !v)}
          >
            <HelpCircle size={20} />
            {supportCount > 0 && user?.role === "superadmin" && (
              <span className="topbar-badge">{supportCount}</span>
            )}
          </button>
        </div>
        {messageOpen && isDesktop && (
          <div className="notif-panel message-panel">
            <div className="notif-header">Inbox</div>
            {messageSessions.length === 0 ? (
              <div className="notif-empty">No direct messages yet.</div>
            ) : (
              <div className="notif-scroll">
                {messageSessions.map((s) => (
                  <button
                    key={s.id || s.createdAt}
                    className="notif-item"
                    onClick={() => {
                      markSessionThreadViewed(s);
                      setPopupSession(s);
                      setPopupCollapsed(false);
                      setMessageOpen(false);
                    }}
                  >
                    <div className="notif-title">{getSessionTitle(s)}</div>
                    <div className="notif-meta">{getSessionPreview(s)}</div>
                  </button>
                ))}
                <button
                  type="button"
                  className="notif-item notif-more"
                  onClick={() => {
                    setMessageOpen(false);
                    window.location.href = "/inbox";
                  }}
                >
                  Open inbox
                </button>
              </div>
            )}
          </div>
        )}
        {!isDesktop && null}
      </div>
      )}

      {iconRefreshHint && !isDesktop && (
        <div className="nia-alert nia-alert--app-update">
          <div>
            <strong>App updated:</strong> If your home-screen icon still looks old, remove and re-add the app once.
          </div>
          <button
            className="btn btn-ghost"
            onClick={() => {
              setIconRefreshHint(false);
              try { localStorage.removeItem(ICON_REFRESH_HINT_KEY); } catch {}
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className={`dashboard-container${isImmersiveChatRoute ? " dashboard-container--chat" : ""}`}>
        {!isImmersiveChatRoute && (
        <div className={`sidebar-drawer${navOpen ? " open" : ""}${isDesktop ? " desktop" : ""}`}>
          <Sidebar
            onNavigate={() => !isDesktop && setNavOpen(false)}
            orgId={orgId}
            orgLogo={primaryLogo}
            orgIcon={compactLogo}
            orgName={orgProfile?.name}
            mode={mode}
            isMobile={!isDesktop}
          />
        </div>
        )}
        {!isImmersiveChatRoute && navOpen && !isDesktop && <div className="sidebar-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />}

        <main className={`dashboard-content${isImmersiveChatRoute ? " dashboard-content--chat" : ""}`}>
          {!isImmersiveChatRoute && (
          <div className="dashboard-topbar">
            <button
              type="button"
              className="icon-btn"
              aria-label="Inbox"
              onClick={() => {
                setMessageOpen((v) => !v);
                setNotifOpen(false);
              }}
            >
              <MessageCircle size={20} />
              {messageCount > 0 && <span className="topbar-badge">{messageCount}</span>}
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label="Notifications"
              onClick={() => setNotifOpen((v) => !v)}
            >
              <Bell size={20} />
              {unreadCount > 0 && <span className="topbar-badge">{unreadCount}</span>}
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label="Support"
              onClick={() => setSupportOpen((v) => !v)}
            >
              <HelpCircle size={20} />
              {supportCount > 0 && user?.role === "superadmin" && (
                <span className="topbar-badge">{supportCount}</span>
              )}
            </button>
            {messageOpen && isDesktop && (
              <div className="notif-panel message-panel">
                <div className="notif-header">Inbox</div>
                {messageSessions.length === 0 ? (
                  <div className="notif-empty">No direct messages yet.</div>
                ) : (
                  <div className="notif-scroll">
                    {messageSessions.map((s) => (
                      <button
                        key={s.id || s.createdAt}
                        className="notif-item"
                        onClick={() => {
                          markSessionThreadViewed(s);
                          setPopupSession(s);
                          setPopupCollapsed(false);
                          setMessageOpen(false);
                        }}
                      >
                        <div className="notif-title">{getSessionTitle(s)}</div>
                        <div className="notif-meta">{getSessionPreview(s)}</div>
                      </button>
                    ))}
                    <button
                      type="button"
                    className="notif-item notif-more"
                    onClick={() => {
                      setMessageOpen(false);
                      navigate("/inbox");
                    }}
                  >
                    Open inbox
                  </button>
                  </div>
                )}
              </div>
            )}
            {notifOpen && isDesktop && (
              <div className="notif-panel">
                <div className="notif-header">Notifications</div>
                {notificationRows.length === 0 ? (
                  <div className="notif-empty">No new notifications.</div>
                ) : (
                  notificationRows.map((row) => (
                    <div
                      key={row.id || row.createdAt}
                      className="notif-item"
                    >
                      <button
                        type="button"
                        className="notif-item-main"
                        onClick={() => {
                          setNotifOpen(false);
                          handleNotifTap(row);
                        }}
                      >
                        <div className="notif-title">{getNotificationTitle(row, user)}</div>
                        <div className="notif-meta">{getNotificationMeta(row, user)}</div>
                      </button>
                      <button
                        type="button"
                        className="notif-dismiss-btn"
                        aria-label={`Dismiss notification: ${getNotificationTitle(row, user)}`}
                        title="Dismiss"
                        onClick={(event) => handleNotifDismiss(event, row)}
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          )}
          {!orgAccessReady ? (
            <section className="nia-card" role="status" aria-live="polite">
              <h2>Organisation access required</h2>
              <p className="nia-muted">
                {orgLoadError || orgSelectionError || selectorPresentation.statusMessage ||
                  "Choose an authorised organisation from the selector to load dashboard data."}
              </p>
            </section>
          ) : (
            <Outlet />
          )}
          {!isImmersiveChatRoute && (
          <footer className="app-footer">
            <div>© {new Date().getFullYear()} Nia COACH by xX Developments. All rights reserved. v{appVersion}</div>
            <div>
              <a href="/privacy">Privacy Policy</a> · <a href="/terms">Terms of Service</a>
            </div>
          </footer>
          )}
        </main>

        {!isDesktop && !isImmersiveChatRoute && (
          <nav className="mobile-bottom-nav" aria-label="Primary">
            {mobileNavItems.map((item) => {
              const active = item.path && location.pathname.startsWith(item.path);
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`mobile-nav-item${active ? " active" : ""}`}
                  onClick={() => {
                    if (item.action) {
                      item.action();
                      return;
                    }
                    if (item.path) navigate(item.path);
                  }}
                >
                  <span className="mobile-nav-icon">{item.icon}</span>
                  <span className="mobile-nav-label">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="mobile-nav-badge">{item.badge > 9 ? "9+" : item.badge}</span>
                  )}
                </button>
              );
            })}
          </nav>
        )}
      </div>

      {supportOpen && (
        <div className="nia-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="support-title" onClick={() => setSupportOpen(false)}>
          <div className="nia-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nia-modal-header">
              <h2 id="support-title">Report an issue</h2>
              <p>Tell us what’s wrong and we’ll respond as soon as possible.</p>
              <button type="button" className="icon-btn" aria-label="Close" onClick={() => setSupportOpen(false)}>
                ×
              </button>
            </div>
            <div className="nia-modal-body">
              <label>Category</label>
              <select value={supportCategory} onChange={(e) => setSupportCategory(e.target.value)}>
                <option value="Bug">Bug</option>
                <option value="Access">Access</option>
                <option value="Billing">Billing</option>
                <option value="Feedback">Feedback</option>
                <option value="Other">Other</option>
              </select>
              <label>Subject</label>
              <input
                type="text"
                value={supportSubject}
                onChange={(e) => setSupportSubject(e.target.value)}
                placeholder="Short summary"
              />
              <label>Message</label>
              <textarea
                rows={4}
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                placeholder="Describe the issue..."
              />
              {supportStatus && <div className="nia-muted" style={{ marginTop: 8 }}>{supportStatus}</div>}
            </div>
            <div className="nia-modal-footer">
              {user?.role === "superadmin" && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setSupportOpen(false);
                    window.location.href = "/support";
                  }}
                >
                  Open support inbox
                </button>
              )}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setSupportOpen(false);
                  window.location.href = "/support/my";
                }}
              >
                View my tickets
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setSupportOpen(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={supportSending} onClick={submitSupport}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {popupSession && (
        <div className={`message-popup${popupCollapsed ? " collapsed" : ""}`}>
          <div className="message-popup-header">
            <div>
              <div className="message-popup-title">{getSessionTitle(popupSession)}</div>
              <div className="message-popup-meta">{getSessionTime(popupSession)}</div>
            </div>
            <div className="message-popup-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPopupCollapsed((v) => !v)}
              >
                {popupCollapsed ? "Expand" : "Collapse"}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPopupSession(null)}
              >
                Close
              </button>
            </div>
          </div>
          {!popupCollapsed && (
            <div className="message-popup-body">
              <div className="message-popup-preview">
                {popupThread.length ? (
                  popupThread.slice(-8).map((msg, idx) => (
                    <div key={`${msg.createdAt || idx}-${idx}`} className="message-popup-row">
                      <span className="message-popup-sender">{getCoachThreadSenderLabel(msg, popupSession, user?.role || "member")}:</span>{" "}
                      <span>{msg.text || ""}</span>
                    </div>
                  ))
                ) : (
                  getSessionPreview(popupSession)
                )}
              </div>
              <textarea
                className="message-popup-input"
                rows={2}
                value={popupInput}
                onChange={(e) => setPopupInput(e.target.value)}
                placeholder="Reply…"
              />
              <div className="message-popup-actions-row">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!popupInput.trim() || popupSending}
                  onClick={async () => {
                    const orgId = dataOrgId;
                    const id = popupSession.id || popupSession.createdAt;
                    if (!orgId || !id || !popupInput.trim()) return;
                    setPopupSending(true);
                    await sendCoachThreadMessage(user, orgId, id, popupInput.trim());
                    setPopupInput("");
                    setPopupSending(false);
                  }}
                >
                  {popupSending ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
